import React from 'react';

import { Divider, Skeleton, Stack, Title, createStyles } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import { useAuth } from 'context/auth-context';
import useAthletesAtRisk from 'hooks/reports/athletes/useAthletesAtRisk';
import useAthletesOnHold from 'hooks/reports/athletes/useAthletesOnHold';
import useCancelledMembers from 'hooks/reports/athletes/useCancelledMembers';
import useCancelledTrialMembers from 'hooks/reports/athletes/useCancelledTrialMembers';
import useMissingAthletesTotalCount from 'hooks/reports/athletes/useMissingAthletesTotalCount';
import useNewMembers from 'hooks/reports/athletes/useNewMembers';
import usePayingMembers from 'hooks/reports/athletes/usePayingMembers';
import useReactivatedMembers from 'hooks/reports/athletes/useReactivatedMembers';
import useTotalMembers from 'hooks/reports/athletes/useTotalMembers';
import useTrialMembers from 'hooks/reports/athletes/useTrialMembers';
import { useNavigate } from 'react-router-dom';
import { getMonthRangeDatesMTD, setDateDifference } from 'utils/date-functions';

import { useDashboardStyles } from '../styles';

const useStyles = createStyles(theme => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: `repeat(5, 1fr)`,
    gridTemplateRows: 'auto',
    gridTemplateAreas: `
        "total paying new cancelled reactivated"
        "hold risk missing trial cancelledTrial"
        `,
    gap: theme.spacing.sm,
    padding: theme.spacing.md,
    [theme.fn.smallerThan('lg')]: {
      gridTemplateColumns: `repeat(5, 1fr)`,
      gridTemplateAreas: `
          "total paying new cancelled reactivated"
          "hold risk missing trial cancelledTrial"
          `,
    },
    [theme.fn.smallerThan('sm')]: {
      gridTemplateColumns: 'auto auto',
      gridTemplateAreas: `
          "total paying"
          "new cancelled"
          "reactivated hold"
          "risk missing"
          "trial cancelledTrial"
          `,
    },
  },
  total: {
    gridArea: 'total',
  },
  paying: {
    gridArea: 'paying',
  },
  new: {
    gridArea: 'new',
  },
  cancelled: {
    gridArea: 'cancelled',
  },
  cancelledTrial: {
    gridArea: 'cancelledTrial',
  },
  reactivated: {
    gridArea: 'reactivated',
  },
  trial: {
    gridArea: 'trial',
  },
  missing: {
    gridArea: 'missing',
  },
  hold: {
    gridArea: 'hold',
  },
  risk: {
    gridArea: 'risk',
  },
}));

const AthletesCard = (): JSX.Element => {
  const { classes: dashboard } = useDashboardStyles();
  const { classes } = useStyles();

  const navigate = useNavigate();

  const { hasPerm } = useAuth();
  const isAthletesViewable = hasPerm('AthleteReportsView');

  const [monthStartDate, todayEnd] = getMonthRangeDatesMTD();
  const twoWeeksFromNow = setDateDifference(todayEnd, 0, 0, 14);

  const {
    data: totalMembers,
    isLoading: totalMembersIsLoading,
    isError: totalMembersIsError,
    isSuccess: totalMembersIsSuccess,
  } = useTotalMembers({
    date: todayEnd.toISOString(),
    enabled: isAthletesViewable,
  });
  const {
    data: payingMembers,
    isLoading: payingMembersIsLoading,
    isError: payingMembersIsError,
    isSuccess: payingMembersIsSuccess,
  } = usePayingMembers({
    date: todayEnd.toISOString(),
    enabled: isAthletesViewable,
  });
  const {
    data: newMembers,
    isLoading: newMembersIsLoading,
    isError: newMembersIsError,
    isSuccess: newMembersIsSuccess,
  } = useNewMembers({
    dateStart: monthStartDate.toISOString(),
    dateEnd: todayEnd.toISOString(),
    enabled: isAthletesViewable,
  });
  const {
    data: reactivatedMembers,
    isLoading: reactivatedMembersIsLoading,
    isError: reactivatedMembersIsError,
    isSuccess: reactivatedMembersIsSuccess,
  } = useReactivatedMembers({
    dateStart: monthStartDate.toISOString(),
    dateEnd: todayEnd.toISOString(),
    enabled: isAthletesViewable,
  });
  const {
    data: trialMembers,
    isLoading: trialMembersIsLoading,
    isError: trialMembersIsError,
    isSuccess: trialMembersIsSuccess,
  } = useTrialMembers({
    dateStart: monthStartDate.toISOString(),
    dateEnd: todayEnd.toISOString(),
    enabled: isAthletesViewable,
  });
  const {
    data: cancelledTrialMembers,
    isLoading: cancelledTrialMembersIsLoading,
    isError: cancelledTrialMembersIsError,
    isSuccess: cancelledTrialMembersIsSuccess,
  } = useCancelledTrialMembers({
    dateStart: monthStartDate.toISOString(),
    dateEnd: todayEnd.toISOString(),
    enabled: isAthletesViewable,
  });
  const {
    data: athletesOnHold,
    isLoading: athletesOnHoldIsLoading,
    isError: athletesOnHoldIsError,
    isSuccess: athletesOnHoldIsSuccess,
  } = useAthletesOnHold({
    date: todayEnd.toISOString(),
    enabled: isAthletesViewable,
  });
  const {
    data: athletesAtRisk,
    isLoading: athletesAtRiskIsLoading,
    isError: athletesAtRiskIsError,
    isSuccess: athletesAtRiskIsSuccess,
  } = useAthletesAtRisk({
    date: twoWeeksFromNow.toISOString(),
    enabled: isAthletesViewable,
  });
  const {
    data: missingAthletes,
    isLoading: missingAthletesIsLoading,
    isError: missingAthletesIsError,
    isSuccess: missingAthletesIsSuccess,
  } = useMissingAthletesTotalCount({ enabled: isAthletesViewable });
  const {
    data: cancelledMembers,
    isLoading: cancelledMembersIsLoading,
    isError: cancelledMembersIsError,
    isSuccess: cancelledMembersIsSuccess,
  } = useCancelledMembers({
    dateStart: monthStartDate.toISOString(),
    dateEnd: todayEnd.toISOString(),
    enabled: isAthletesViewable,
  });

  const handleReportClick = (report: string): void => {
    if (isAthletesViewable) {
      navigate(`/reports/${report}`);
    }
  };

  const getTotalMembersCount = (): JSX.Element => {
    if (!isAthletesViewable) {
      return <IconLock size='2rem' />;
    }
    if (totalMembersIsLoading) {
      return <Skeleton height='2rem' width='75%' />;
    }
    if (totalMembersIsError) {
      return <div className={dashboard.value}>Error</div>;
    }
    if (totalMembersIsSuccess && totalMembers) {
      return <div className={dashboard.value}>{totalMembers.pages[0].data.count}</div>;
    }
    return <></>;
  };

  const getTrialMembersCount = (): JSX.Element => {
    if (!isAthletesViewable) {
      return <IconLock size='2rem' />;
    }
    if (trialMembersIsLoading) {
      return <Skeleton height='2rem' width='75%' />;
    }
    if (trialMembersIsError) {
      return <div className={dashboard.value}>Error</div>;
    }
    if (trialMembersIsSuccess && trialMembers) {
      return <div className={dashboard.value}>{trialMembers.pages[0].data.count}</div>;
    }
    return <></>;
  };

  const getPayingMembersCount = (): JSX.Element => {
    if (!isAthletesViewable) {
      return <IconLock size='2rem' />;
    }
    if (payingMembersIsLoading) {
      return <Skeleton height='2rem' width='75%' />;
    }
    if (payingMembersIsError) {
      return <div className={dashboard.value}>Error</div>;
    }
    if (payingMembersIsSuccess && payingMembers) {
      return <div className={dashboard.value}>{payingMembers.pages[0].data.count}</div>;
    }
    return <></>;
  };

  const getNewMembersCount = (): JSX.Element => {
    if (!isAthletesViewable) {
      return <IconLock size='2rem' />;
    }
    if (newMembersIsLoading) {
      return <Skeleton height='2rem' width='75%' />;
    }
    if (newMembersIsError) {
      return <div className={dashboard.value}>Error</div>;
    }
    if (newMembersIsSuccess && newMembers) {
      return <div className={dashboard.value}>{newMembers.pages[0].data.count}</div>;
    }
    return <></>;
  };

  const getAthletesOnHoldCount = (): JSX.Element => {
    if (!isAthletesViewable) {
      return <IconLock size='2rem' />;
    }
    if (athletesOnHoldIsLoading) {
      return <Skeleton height='2rem' width='75%' />;
    }
    if (athletesOnHoldIsError) {
      return <div className={dashboard.value}>Error</div>;
    }
    if (athletesOnHoldIsSuccess && athletesOnHold) {
      return <div className={dashboard.value}>{athletesOnHold.pages[0].data.count}</div>;
    }
    return <></>;
  };

  const getAthletesAtRiskCount = (): JSX.Element => {
    if (!isAthletesViewable) {
      return <IconLock size='2rem' />;
    }
    if (athletesAtRiskIsLoading) {
      return <Skeleton height='2rem' width='75%' />;
    }
    if (athletesAtRiskIsError) {
      return <div className={dashboard.value}>Error</div>;
    }
    if (athletesAtRiskIsSuccess && athletesAtRisk) {
      return <div className={dashboard.value}>{athletesAtRisk.pages[0].data.count}</div>;
    }
    return <></>;
  };

  const getMissingAthletesCount = (): JSX.Element => {
    if (!isAthletesViewable) {
      return <IconLock size='2rem' />;
    }
    if (missingAthletesIsLoading) {
      return <Skeleton height='2rem' width='75%' />;
    }
    if (missingAthletesIsError) {
      return <div className={dashboard.value}>Error</div>;
    }
    if (missingAthletesIsSuccess && missingAthletes) {
      return <div className={dashboard.value}>{missingAthletes.data.count}</div>;
    }
    return <></>;
  };

  const getReactivatedMembersCount = (): JSX.Element => {
    if (!isAthletesViewable) {
      return <IconLock size='2rem' />;
    }
    if (reactivatedMembersIsLoading) {
      return <Skeleton height='2rem' width='75%' />;
    }
    if (reactivatedMembersIsError) {
      return <div className={dashboard.value}>Error</div>;
    }
    if (reactivatedMembersIsSuccess && reactivatedMembers) {
      return <div className={dashboard.value}>{reactivatedMembers.pages[0].data.count}</div>;
    }
    return <></>;
  };

  const getCancelledTrialMembersCount = (): JSX.Element => {
    if (!isAthletesViewable) {
      return <IconLock size='2rem' />;
    }
    if (cancelledTrialMembersIsLoading) {
      return <Skeleton height='2rem' width='75%' />;
    }
    if (cancelledTrialMembersIsError) {
      return <div className={dashboard.value}>Error</div>;
    }
    if (cancelledTrialMembersIsSuccess && cancelledTrialMembers) {
      return <div className={dashboard.value}>{cancelledTrialMembers.pages[0].data.count}</div>;
    }
    return <></>;
  };

  const getCancelledMembersCount = (): JSX.Element => {
    if (!isAthletesViewable) {
      return <IconLock size='2rem' />;
    }
    if (cancelledMembersIsLoading) {
      return <Skeleton height='2rem' width='75%' />;
    }
    if (cancelledMembersIsError) {
      return <div className={dashboard.value}>Error</div>;
    }
    if (cancelledMembersIsSuccess && cancelledMembers) {
      return <div className={dashboard.value}>{cancelledMembers.pages[0].data.count}</div>;
    }
    return <></>;
  };

  return (
    <div className={`${dashboard.athletes} ${dashboard.card}`}>
      <div className={dashboard.cardHeader}>
        <Title order={4}>Athletes</Title>
      </div>

      <Divider />

      <div className={classes.grid}>
        <Stack
          spacing={0}
          p='xs'
          className={`${classes.total} ${isAthletesViewable ? dashboard.clickableCard : undefined}`}
          onClick={() => handleReportClick('total-members')}
        >
          <div className={dashboard.label}>
            <Stack spacing={0}>
              <div>Total</div>
              <div>Members</div>
            </Stack>
          </div>
          {getTotalMembersCount()}
        </Stack>

        <Stack
          spacing={0}
          p='xs'
          className={`${classes.paying} ${isAthletesViewable ? dashboard.clickableCard : undefined}`}
          onClick={() => handleReportClick('paying-members')}
        >
          <div className={dashboard.label}>
            <Stack spacing={0}>
              <div>Paying</div>
              <div>Members</div>
            </Stack>
          </div>
          {getPayingMembersCount()}
        </Stack>

        <Stack
          spacing={0}
          p='xs'
          className={`${classes.new} ${isAthletesViewable ? dashboard.clickableCard : undefined}`}
          onClick={() => handleReportClick('new-members')}
        >
          <div className={dashboard.label}>
            <Stack spacing={0}>
              <div>New</div>
              <div>Members MTD</div>
            </Stack>
          </div>
          {getNewMembersCount()}
        </Stack>

        <Stack
          spacing={0}
          p='xs'
          className={`${classes.cancelled} ${isAthletesViewable ? dashboard.clickableCard : undefined}`}
          onClick={() => handleReportClick('cancelled-members')}
        >
          <div className={dashboard.label}>
            <Stack spacing={0}>
              <div>Cancelled</div>
              <div>Members MTD</div>
            </Stack>
          </div>
          {getCancelledMembersCount()}
        </Stack>
        <Stack
          spacing={0}
          p='xs'
          className={`${classes.missing} ${isAthletesViewable ? dashboard.clickableCard : undefined}`}
          onClick={() => handleReportClick('missing-athletes')}
        >
          <div className={dashboard.label}>
            <Stack spacing={0}>
              <div>Missing</div>
              <div>Athletes</div>
            </Stack>
          </div>
          {getMissingAthletesCount()}
        </Stack>

        <Stack
          spacing={0}
          p='xs'
          className={`${classes.hold} ${isAthletesViewable ? dashboard.clickableCard : undefined}`}
          onClick={() => handleReportClick('athletes-on-hold')}
        >
          <div className={dashboard.label}>
            <Stack spacing={0}>
              <div>Athletes</div>
              <div>On Hold</div>
            </Stack>
          </div>
          {getAthletesOnHoldCount()}
        </Stack>

        <Stack
          spacing={0}
          p='xs'
          className={`${classes.risk} ${isAthletesViewable ? dashboard.clickableCard : undefined}`}
          onClick={() => handleReportClick('athletes-at-risk')}
        >
          <div className={dashboard.label}>
            <Stack spacing={0}>
              <div>Athletes</div>
              <div>At Risk</div>
            </Stack>
          </div>
          {getAthletesAtRiskCount()}
        </Stack>
        <Stack
          spacing={0}
          p='xs'
          className={`${classes.reactivated} ${isAthletesViewable ? dashboard.clickableCard : undefined}`}
          onClick={() => handleReportClick('reactivated-members')}
        >
          <div className={dashboard.label}>
            <Stack spacing={0}>
              <div>Reactivated</div>
              <div>Members MTD</div>
            </Stack>
          </div>
          {getReactivatedMembersCount()}
        </Stack>
        <Stack
          spacing={0}
          p='xs'
          className={`${classes.trial} ${isAthletesViewable ? dashboard.clickableCard : undefined}`}
          onClick={() => handleReportClick('trial-members')}
        >
          <div className={dashboard.label}>
            <Stack spacing={0}>
              <div>New</div>
              <div>Trials MTD</div>
            </Stack>
          </div>
          {getTrialMembersCount()}
        </Stack>
        <Stack
          spacing={0}
          p='xs'
          className={`${classes.cancelledTrial} ${isAthletesViewable ? dashboard.clickableCard : undefined}`}
          onClick={() => handleReportClick('cancelled-trial-members')}
        >
          <div className={dashboard.label}>
            <Stack spacing={0}>
              <div>Cancelled</div>
              <div>Trials MTD</div>
            </Stack>
          </div>
          {getCancelledTrialMembersCount()}
        </Stack>
      </div>
    </div>
  );
};

export default AthletesCard;
